import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Editable, EditableImage } from "@/components/Editable";
import { useApp } from "@/context/AppContext";

const HERO_INITIAL = { opacity: 0, y: 30 };
const HERO_ANIMATE = { opacity: 1, y: 0 };
const HERO_TRANSITION = { duration: 0.7, ease: [0.22, 1, 0.36, 1] };

function keyFromPath(pathname) {
  const p = pathname.replace(/\//g, "_").replace(/^_/, "") || "home";
  return `page_${p}`;
}

export function PageHero({ image, video, poster, eyebrow, title, subtitle, children, tall = false, top = false, showcase = null, idBase }) {
  const location = useLocation();
  const { editMode } = useApp();
  const base = idBase || keyFromPath(location.pathname);
  const [showVideo, setShowVideo] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    setShowVideo(mq.matches);
    const onChange = (e) => setShowVideo(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return (
    <section className={`relative overflow-hidden grain ${tall ? "min-h-[82vh]" : "min-h-[56vh]"} flex ${top ? "items-start" : "items-center"}`} data-testid="page-hero">
      <div className="absolute inset-0">
        <EditableImage id={`${base}.hero_image`} src={video && !editMode ? (poster || image) : image} alt={title} className="w-full h-full" />
        {video && !editMode && showVideo && (
          <video
            autoPlay muted loop playsInline preload="auto"
            poster={poster || image}
            ref={(el) => { if (el) el.play().catch(() => {}); }}
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            data-testid="page-hero-video"
          >
            <source src={video} type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-[#014845] via-[#014845]/85 to-[#017471]/35 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#014845]/90 via-transparent to-[#014845]/50 pointer-events-none" />
      </div>
      <div className={`relative oj-container on-dark ${top ? "pt-10 pb-20" : "py-20"}`}>
        {showcase ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            <motion.div initial={HERO_INITIAL} animate={HERO_ANIMATE} transition={HERO_TRANSITION} className="max-w-3xl lg:col-span-7">
              {eyebrow && <div className="oj-eyebrow mb-4 !text-teal-200"><span className="w-6 h-px bg-teal-200" /> <Editable id={`${base}.hero_eyebrow`}>{eyebrow}</Editable></div>}
              <Editable as="h1" id={`${base}.hero_title`} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white block">{title}</Editable>
              {subtitle && <Editable as="p" id={`${base}.hero_subtitle`} className="mt-6 text-lg text-teal-50/90 leading-relaxed max-w-2xl block">{subtitle}</Editable>}
              {children && <div className="mt-8 flex flex-wrap gap-4 items-center">{children}</div>}
            </motion.div>
            <div className="lg:col-span-5">{showcase}</div>
          </div>
        ) : (
          <motion.div initial={HERO_INITIAL} animate={HERO_ANIMATE} transition={HERO_TRANSITION} className="max-w-3xl">
            {eyebrow && <div className="oj-eyebrow mb-4 !text-teal-200"><span className="w-6 h-px bg-teal-200" /> <Editable id={`${base}.hero_eyebrow`}>{eyebrow}</Editable></div>}
            <Editable as="h1" id={`${base}.hero_title`} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white block">{title}</Editable>
            {subtitle && <Editable as="p" id={`${base}.hero_subtitle`} className="mt-6 text-lg text-teal-50/90 leading-relaxed max-w-2xl block">{subtitle}</Editable>}
            {children && <div className="mt-8 flex flex-wrap gap-4">{children}</div>}
          </motion.div>
        )}
      </div>
    </section>
  );
}

export function SectionHeader({ eyebrow, title, subtitle, center = true, idBase }) {
  const location = useLocation();
  const slug = (title || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 30);
  const base = `${idBase || keyFromPath(location.pathname)}_sec_${slug}`;
  return (
    <div className={`mb-12 ${center ? "text-center mx-auto max-w-2xl" : "max-w-2xl"}`}>
      {eyebrow && <div className={`oj-eyebrow mb-3 ${center ? "justify-center" : ""}`}><Editable id={`${base}.eyebrow`}>{eyebrow}</Editable></div>}
      <Editable as="h2" id={`${base}.title`} className="oj-h2 block">{title}</Editable>
      {subtitle && <Editable as="p" id={`${base}.subtitle`} className="mt-4 text-slate-600 leading-relaxed block">{subtitle}</Editable>}
    </div>
  );
}

export default PageHero;
